import { inject, Service } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { User } from '../model/user';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { Router } from '@angular/router';
import { jwtDecode as JWT } from 'jwt-decode';
import { LoginUser } from '../model/login-user';
import { BexioService } from '../services/bexio.service';
import { map } from 'rxjs/operators';
import { authTokenStorageKey } from '../app.constants';

@Service()
export class Auth2Service {
  readonly #bexioService = inject(BexioService);
  readonly #http = inject(HttpClient);
  readonly #router = inject(Router);

  readonly #isConnected: BehaviorSubject<boolean> = new BehaviorSubject(false);
  readonly #userSubject = new BehaviorSubject<User | null>(null);

  token = new Subject<string | undefined>();
  loggedUser?: User;
  redirectUrl?: string;

  isLoggedIn = false;

  constructor() {
    this.token.subscribe(token => {
      this.loadConnectedUser(token);
    });
  }

  user(): Observable<User | null> {
    return this.#userSubject.asObservable();
  }

  connected(): Observable<boolean> {
    return this.#isConnected.asObservable();
  }

  login(username: string, password: string) {
    return this.#http
      .post('/jwtlogin', new LoginUser(username, password), {
        observe: 'response',
        responseType: 'text'
      })
      .pipe(
        map(response => {
          let jwtToken = response.headers.get('Authorization');
          if (jwtToken) {
            // store jwt token in local storage to keep user logged in between page refreshes
            localStorage.setItem(authTokenStorageKey, jwtToken);
            this.token.next(jwtToken);
          }
        })
      );
  }

  isTokenExpired() {
    let strToken = localStorage.getItem(authTokenStorageKey);
    if (strToken) {
      let myToken: any = JWT(strToken);
      let current_time = Date.now() / 1000;
      if (current_time > myToken.exp) {
        return true;
      }
    }
    return false;
  }

  logout() {
    // remove user from local storage to log user out
    this.#http.get('/api/bexio/logout').subscribe({
      next: data => {
        localStorage.removeItem(authTokenStorageKey);
        this.#isConnected.next(false);
        this.token.next(undefined);
        this.#userSubject.next(null);
        this.isLoggedIn = false;

        // get the new grant URL
        this.#bexioService.statusSubject.next(data);

        void this.#router.navigate(['/login']);
      },
      error: () => {
        // If logout fails, force token reload
        localStorage.removeItem(authTokenStorageKey);
      }
    });
  }

  loadUser() {
    this.token.next(localStorage.getItem(authTokenStorageKey) ?? undefined);
  }

  private loadConnectedUser(token?: string) {
    if (token && token !== '') {
      this.#http.get('/api/bexio/user').subscribe(user => {
        this.loggedUser = new User(user);
        this.#isConnected.next(true);
        this.#userSubject.next(this.loggedUser);
        this.isLoggedIn = true;

        if (this.redirectUrl) {
          void this.#router.navigate([this.redirectUrl]);
          this.redirectUrl = undefined;
        }
      });
    }
  }
}
